import { Box, useColorMode } from "@chakra-ui/react";
import { CSSProperties, useId } from "react";

export type HeroTextureVariant = "mesh";

type ThemeMode = "light" | "dark";

/**
 * Per-theme mesh colors and visibility.
 * `primary` colors the main connection lines.
 * `secondary` colors the softer offset/drift lines.
 * `node` colors the mesh points.
 * `layerOpacity` controls the overall visibility of the mesh layer.
 */
export interface HeroTextureLayerThemeConfig {
  layerOpacity?: number;
  primary?: string;
  secondary?: string;
  node?: string;
}

/**
 * Theme-aware configuration for the mesh layer.
 * Use `light` and `dark` to tune the texture independently per theme.
 */
export interface HeroTextureLayerConfig {
  light?: HeroTextureLayerThemeConfig;
  dark?: HeroTextureLayerThemeConfig;
}

/**
 * Per-theme glow settings for the soft radial wash behind the mesh.
 * `opacity` controls glow visibility and `color` controls the glow tint.
 */
export interface HeroTextureGlowThemeConfig {
  opacity?: number;
  color?: string;
}

/**
 * Theme-aware glow configuration.
 */
export interface HeroTextureGlowConfig {
  light?: HeroTextureGlowThemeConfig;
  dark?: HeroTextureGlowThemeConfig;
}

/**
 * Theme-aware blend mode configuration.
 * Useful when the same mesh needs different compositing behavior in light
 * and dark themes.
 */
export interface HeroTextureBlendConfig {
  light?: CSSProperties["mixBlendMode"];
  dark?: CSSProperties["mixBlendMode"];
}

/**
 * Controls the slow continuous background movement.
 * `duration` is in seconds and should stay large for a calm feel.
 * `driftX` and `driftY` are the maximum translation distances in pixels.
 * `scale` adds a very slight breathing effect while the mesh drifts.
 */
export interface HeroTextureAnimationConfig {
  duration?: number;
  driftX?: number;
  driftY?: number;
  scale?: number;
}

/**
 * Mesh-only hero background texture.
 *
 * Props:
 * `variant`: kept for API compatibility, only `"mesh"` is supported now.
 * `patternConfig`: light/dark colors and opacity for the mesh layer.
 * `glowConfig`: light/dark glow color and opacity behind the mesh.
 * `blendMode`: light/dark blend mode used to composite the mesh over the hero.
 * `animationConfig`: controls the very slow infinite background drift.
 */
export interface HeroTextureProps {
  variant?: HeroTextureVariant;
  patternConfig?: HeroTextureLayerConfig;
  glowConfig?: HeroTextureGlowConfig;
  blendMode?: HeroTextureBlendConfig;
  animationConfig?: HeroTextureAnimationConfig;
}

const DEFAULT_PATTERN_CONFIG: Record<
  ThemeMode,
  Required<HeroTextureLayerThemeConfig>
> = {
  light: {
    layerOpacity: 0.8,
    primary: "#7896c5",
    secondary: "#a9bddf",
    node: "#b7c8e2",
  },
  dark: {
    layerOpacity: 0.2,
    primary: "#f3f4f6",
    secondary: "#f6d0cf",
    node: "#f6d0cf",
  },
};

const DEFAULT_GLOW_CONFIG: Record<
  ThemeMode,
  Required<HeroTextureGlowThemeConfig>
> = {
  light: {
    opacity: 0.12,
    color: "rgba(173, 11, 6, 0.06)",
  },
  dark: {
    opacity: 0.12,
    color: "rgba(249, 168, 166, 0.06)",
  },
};

const DEFAULT_BLEND_MODE: Record<ThemeMode, CSSProperties["mixBlendMode"]> = {
  light: "multiply",
  dark: "screen",
};

const DEFAULT_ANIMATION_CONFIG: Required<HeroTextureAnimationConfig> = {
  duration: 8,
  driftX: 8,
  driftY: 10,
  scale: 1.015,
};

export const HeroTexture = ({
  variant = "mesh",
  patternConfig,
  glowConfig,
  blendMode,
  animationConfig,
}: HeroTextureProps) => {
  const id = useId().replace(/:/g, "");
  const { colorMode } = useColorMode();
  const mode: ThemeMode = colorMode === "dark" ? "dark" : "light";

  const activePattern = {
    ...DEFAULT_PATTERN_CONFIG[mode],
    ...patternConfig?.[mode],
  };
  const activeGlow = {
    ...DEFAULT_GLOW_CONFIG[mode],
    ...glowConfig?.[mode],
  };
  const activeBlendMode = blendMode?.[mode] ?? DEFAULT_BLEND_MODE[mode];
  const activeAnimation = {
    ...DEFAULT_ANIMATION_CONFIG,
    ...animationConfig,
  };

  const keyframesName = `hero-mesh-drift-${id}`;
  const fadeKeyframesName = `hero-mesh-fade-${id}`;
  const patternId = `mesh-pattern-${id}`;
  const patternOffsetId = `mesh-pattern-offset-${id}`;
  const patternDriftId = `mesh-pattern-drift-${id}`;

  // `variant` is intentionally retained for a stable API, but mesh is the only
  // supported visual system now.
  void variant;

  return (
    <>
      <style>
        {`
          @keyframes ${fadeKeyframesName} {
            0% {
              opacity: 0;
            }
            100% {
              opacity: ${activePattern.layerOpacity};
            }
          }

          @keyframes ${keyframesName} {
            0% {
              transform: translate(-50%, -50%) translate3d(0px, 0px, 0) scale(1);
            }
            50% {
              transform: translate(-50%, -50%) translate3d(${activeAnimation.driftX}px, -${activeAnimation.driftY}px, 0) scale(${activeAnimation.scale});
            }
            100% {
              transform: translate(-50%, -50%) translate3d(-${activeAnimation.driftX}px, ${activeAnimation.driftY}px, 0) scale(1);
            }
          }
        `}
      </style>

      <Box
        position="absolute"
        inset={0}
        zIndex={1}
        pointerEvents="none"
        overflow="hidden"
        opacity={0}
        animation={`${fadeKeyframesName} 2s ease-in forwards`}
      >
        <Box
          position="absolute"
          top="50%"
          left="50%"
          w={{ base: "1400px", md: "1800px", xl: "2200px" }}
          h={{ base: "900px", md: "1150px", xl: "1400px" }}
          animation={`${keyframesName} ${activeAnimation.duration}s ease-in-out infinite alternate`}
          transformOrigin="center center"
          style={{ mixBlendMode: activeBlendMode }}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 1600 900"
            preserveAspectRatio="xMidYMid slice"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
            }}
          >
            <defs>
              <pattern
                id={patternId}
                width="420"
                height="260"
                patternUnits="userSpaceOnUse"
              >
                <g
                  fill="none"
                  stroke={activePattern.primary}
                  strokeWidth="1"
                  opacity="0.24"
                >
                  <path d="M10 228 86 150 148 178 204 98 278 120 344 58 412 96" />
                  <path d="M34 56 86 150 164 76 204 98 250 34 312 86 412 96" />
                  <path d="M10 228 34 56" />
                  <path d="M148 178 164 76" />
                  <path d="M204 98 278 120" />
                  <path d="M278 120 312 86" />
                  <path d="M344 58 312 86" />
                  <path d="M148 178 204 98 344 58" />
                  <path d="M86 150 204 98 278 120" />
                  <path d="M86 150 148 178 204 98 250 34" />
                </g>
                <g fill={activePattern.node} opacity="0.28">
                  <circle cx="10" cy="228" r="2.2" />
                  <circle cx="34" cy="56" r="2" />
                  <circle cx="86" cy="150" r="2.4" />
                  <circle cx="148" cy="178" r="2.1" />
                  <circle cx="164" cy="76" r="2.2" />
                  <circle cx="204" cy="98" r="2.6" />
                  <circle cx="250" cy="34" r="1.9" />
                  <circle cx="278" cy="120" r="2.2" />
                  <circle cx="312" cy="86" r="2" />
                  <circle cx="344" cy="58" r="2.2" />
                  <circle cx="412" cy="96" r="2" />
                </g>
              </pattern>

              <pattern
                id={patternOffsetId}
                width="420"
                height="260"
                patternUnits="userSpaceOnUse"
                patternTransform="translate(210 130)"
              >
                <g
                  fill="none"
                  stroke={activePattern.primary}
                  strokeWidth="0.85"
                  opacity="0.14"
                >
                  <path d="M10 228 86 150 148 178 204 98 278 120 344 58 412 96" />
                  <path d="M34 56 86 150 164 76 204 98 250 34 312 86 412 96" />
                  <path d="M10 228 34 56" />
                  <path d="M148 178 164 76" />
                  <path d="M86 150 204 98 278 120" />
                  <path d="M148 178 204 98 344 58" />
                </g>
                <g fill={activePattern.node} opacity="0.12">
                  <circle cx="86" cy="150" r="2" />
                  <circle cx="204" cy="98" r="2.1" />
                  <circle cx="344" cy="58" r="1.8" />
                </g>
              </pattern>

              <pattern
                id={patternDriftId}
                width="420"
                height="260"
                patternUnits="userSpaceOnUse"
                patternTransform="translate(96 188)"
              >
                <g
                  fill="none"
                  stroke={activePattern.secondary}
                  strokeWidth="0.7"
                  opacity="0.08"
                >
                  <path d="M10 228 86 150 148 178 204 98 278 120" />
                  <path d="M34 56 86 150 164 76 204 98 250 34" />
                  <path d="M148 178 204 98 344 58" />
                </g>
                <g fill={activePattern.node} opacity="0.08">
                  <circle cx="86" cy="150" r="1.8" />
                  <circle cx="204" cy="98" r="1.9" />
                  <circle cx="278" cy="120" r="1.6" />
                </g>
              </pattern>
            </defs>

            <rect width="1600" height="900" fill={`url(#${patternId})`} />
            <rect width="1600" height="900" fill={`url(#${patternOffsetId})`} />
            <rect width="1600" height="900" fill={`url(#${patternDriftId})`} />
          </svg>
        </Box>
      </Box>

      <Box
        position="absolute"
        inset={0}
        zIndex={1}
        pointerEvents="none"
        opacity={activeGlow.opacity}
        bgImage={`
          radial-gradient(circle at 78% 28%, ${activeGlow.color}, transparent 28%),
          radial-gradient(circle at 18% 76%, ${activeGlow.color}, transparent 24%)
        `}
      />
    </>
  );
};
